import React from 'react';
import { Flex, Card, Button, Space, Input, Pagination, Empty, Grid } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import ChannelCard from '../ChannelCard/ChannelCard';
import ChannelCardSkeleton from '../ChannelCardSkeleton/ChannelCardSkeleton';
import SharedFilterBar from '../../Shared/Filters/SharedFilterBar';
import { channelFilterFields, channelSortFields } from '../../Shared/Filters/filterConfig';

const { useBreakpoint } = Grid;

const ChannelGrid = ({
    channels,
    loading,
    isInitialLoading,
    pagination,
    searchString,
    filters,
    sorter,
    onTableChange,
    onSearch,
    onFilterChange,
    onAction,
    onAdd,
    highlightedRowIds,
    softRefreshKey,
    deleteLoading,
    updateLoading,
}) => {
    const { t } = useTranslation();
    const screens = useBreakpoint() || {};
    const isMobile = !!screens?.xs;

    const handlePaginationChange = (page, pageSize) => {
        onTableChange(
            { current: page, pageSize },
            {},
            { field: sorter.field, order: sorter.order }
        );
    };

    const handleSortChange = ({ sort_by, sort_direction }) => {
        const order = sort_direction === 'asc' ? 'ascend' : sort_direction === 'desc' ? 'descend' : null;
        onTableChange(
            pagination,
            {},
            { field: sort_by, order }
        );
    };

    return (
        <div style={{ padding: '16px' }}>
            <Card
                style={{
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    marginBottom: '16px'
                }}
                title={t('channels')}
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={onAdd}
                    >
                        {!isMobile && t('add_channel')}
                    </Button>
                }
                styles={{
                    body: {
                        padding: 16
                    }
                }}
            >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                    <Space wrap style={{ width: '100%' }} size="middle">
                        <Input
                            prefix={<SearchOutlined />}
                            placeholder={t('search_channels')}
                            value={searchString}
                            onChange={(e) => onSearch(e.target.value)}
                            style={{ width: isMobile ? '100%' : '400px' }}
                            allowClear
                        />
                        <SharedFilterBar
                            fields={channelFilterFields}
                            value={filters}
                            onChange={onFilterChange}
                            sortFields={channelSortFields}
                            sortValue={{ sort_by: sorter.field, sort_direction: sorter.order }}
                            onSortChange={handleSortChange}
                        />
                    </Space>
                </Space>
            </Card>

            {isInitialLoading ? (
                <Flex justify="center" gap={16} wrap>
                    {[...Array(8)].map((_, index) => (
                        <ChannelCardSkeleton key={index} />
                    ))}
                </Flex>
            ) : channels.length === 0 ? (
                <Card>
                    <Empty description={t('no_channels_found')} />
                </Card>
            ) : (
                <>
                    <Flex justify="center" gap={16} wrap>
                        {channels.map((channel) => (
                            <ChannelCard
                                key={channel.id || channel.external_channel_id}
                                channel={channel}
                                onAction={onAction}
                                deleteLoading={deleteLoading}
                                updateLoading={updateLoading}
                            />
                        ))}
                    </Flex>

                    {pagination.total > pagination.pageSize && (
                        <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                            <Pagination
                                current={pagination.current}
                                pageSize={pagination.pageSize}
                                total={pagination.total}
                                onChange={handlePaginationChange}
                                showSizeChanger
                                pageSizeOptions={pagination.pageSizeOptions}
                                showTotal={(total, range) =>
                                    `${range[0]}-${range[1]} ${t('of')} ${total} ${t('items')}`
                                }
                            />
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

export default ChannelGrid;
